import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';

import { getAll } from '../api/ventura/adGroup';
import { PageableList } from '../types/contract';
import mapAdGroupToForm from '../util/mapAdGroupToForm';
import { useConfig } from './useConfig';

function useAdGroups(lineItemId: number, enabled = true, defaultPage = 0, defaultPerPage = 9999) {
  // The IO-nested campaign (== AdGroup) index opts into the per-domain flip:
  // apiHostFor('campaigns') resolves to ACH iff campaigns is flipped ON (migrated
  // list + env gate), else the plain Rails apiHost. The {collection,pagination}
  // envelope is shape-identical for what these consumers read (the `collection`
  // array only — no consumer reads the pagination meta, so RD-3's meta-key
  // changes are invisible), so only the host switches: no response adaptation.
  // CAVEAT (same as useLineItems): this fetches per_page=9999 and pages/filters the
  // full collection client-side, but ACH clamps per_page to 200 (Go
  // pagination.MaxPerPage) — an IO with >200 live campaigns would come back short
  // on the ACH leg. e2e/parity/campaigns.parity.spec.ts pins a length-parity
  // invariant so that truncation is loud.
  const { apiHostFor } = useConfig();
  const campaignsHost = apiHostFor('campaigns');
  const [page, setPage] = useState(defaultPage);
  const [perPage, setPerPage] = useState(defaultPerPage);

  const mapCampaignListToAdGroup = React.useCallback(
    (data: PageableList<any>) => ({
      ...data,
      collection: data.collection?.map(mapAdGroupToForm),
    }),
    []
  );

  const adGroups = useQuery({
    queryKey: ['adGroups', lineItemId, page, perPage],
    queryFn: () => getAll(campaignsHost, lineItemId, { page, perPage }),
    select: mapCampaignListToAdGroup,
    enabled,
  });
  return { page, setPage, perPage, setPerPage, ...adGroups };
}

export default useAdGroups;
